import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import { usePostOneTemplateImageUploadMutation } from "../../../../services/api/repositories/TemplateMailsRepository";
import PostOneImageUploadRequest from "../../../../models/api/Media/PostOneImageUploadRequest";

export default function MediaCreateSection() {
    const navigate = useNavigate();

    const [postOneTemplateImageUpload, {data, isSuccess, isLoading, isError, error}] = usePostOneTemplateImageUploadMutation();
    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{description : string, image : string}>({description : "", image : ""});
    
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const imageRef = useRef<HTMLInputElement>(null);

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");
   
    const handleClose = useCallback(() =>{
        setShow(false)
    }, [setShow]);

    const handleShow = useCallback(async() =>{
        setShowValidation(true);

        if(!descriptionRef.current?.value || !imageRef.current?.files?.[0]){
            return;
        }
        
        let errors = validationErrors;
        let canSend = true;

        if(descriptionRef.current?.value.length > 255){
            errors.description = "La description ne doit pas dépasser 255 caractères";
            canSend = false;
        }

        if(!canSend){
            setValidationErrors(errors);
            return;
        }
 
        setShow(true)
    }, [setShow, descriptionRef, imageRef, validationErrors, setValidationErrors]);

    const handleSubmit = useCallback(async () =>{
        setShowValidation(true);
        if(!descriptionRef.current?.value || !imageRef.current?.files?.[0]){
            return;
        }

        let errors = validationErrors;
        let canSend = true;

        if(descriptionRef.current?.value.length > 255){
            errors.description = "La description ne doit pas dépasser 255 caractères";
            canSend = false;
        }

        if(!canSend){
            setValidationErrors(errors);
            return;
        }
        
        postOneTemplateImageUpload(
            new PostOneImageUploadRequest({
                description: descriptionRef.current?.value,
                image: imageRef.current?.files?.[0]
            })
        );

        setShow(false);
    }, [ setShow, postOneTemplateImageUpload, descriptionRef,  imageRef, validationErrors, setValidationErrors]);

    const handleError = useCallback(() => {
        
        console.log(error);
        if(isError && error !== undefined && error !== null && typeof error === 'object' && (errMsg === "" || errMsg === "Une erreur a été rencontrée")){
            if("error" in error){
                setErrMsg(error.error as string);
            }else if("data" in error && typeof error.data === 'object'&& error.data !== null && "message" in error.data){
                setErrMsg(error.data.message as string);
            }
        }else if(errMsg === ""){
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [isError, error, errMsg]);

    useEffect(() => {
        if(isSuccess){
            navigate("/media?refetch=true");
        }

        if( isError){
            handleError();
        }
    
    }, [isSuccess, navigate, isError, handleError]);
    
    return (
        <>
            <div>
                <div className="object media">
                    {isLoading && (<h3>Enregistrement en cours</h3>)}
                    {!isLoading && !isSuccess &&
                        <Form className="form" validated={showValidation}>
                            <section className="form-section id">
                                <h3>
                                    <span className="icon"></span>
                                    <span className="text">Définition</span>
                                </h3>
                                <Form.Group className="form-group media-description">
                                    <Form.Label className="form-label" htmlFor="media-description-input">Description</Form.Label>
                                    <Form.Control 
                                        ref={descriptionRef} 
                                        required 
                                        as="textarea" 
                                        type="text" 
                                        className="form-control w100" 
                                        id="media-description-input" 
                                        tabIndex={2}
                                        maxLength={255}
                                        isInvalid={validationErrors.description.length > 0}
                                    >
                                    </Form.Control>
                                    <Form.Control.Feedback 
                                        type="invalid">
                                        { validationErrors.description.length > 0 ? validationErrors.description : 
                                        "Veuillez saisir une description"}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </section>

                            <section className="form-section file">
                                <h3>
                                    <span className="icon"></span>
                                    <span className="text">Fichier</span>
                                </h3>
                                <Form.Group className="form-group media-file required">
                                    <Form.Label className="form-label" htmlFor="media-file-input">Fichier</Form.Label>
                                    <Form.Control 
                                        ref={imageRef} 
                                        required type="file" 
                                        className="form-control w100" 
                                        id="media-file-input" 
                                        tabIndex={4}
                                        isInvalid={validationErrors.image.length > 0}
                                        />
                                    <Form.Control.Feedback type="invalid">{validationErrors.image.length > 0 ? validationErrors.image : "Veuillez sélectionner un fichier"}</Form.Control.Feedback>
                                </Form.Group>
                            </section>

                            <div>

                                {isError && data !== undefined &&  "message" in data && (
                                    <Alert
                                        variant="danger" 
                                        dismissible>
                                        {data.message}
                                    </Alert>
                                )}

                                {isError && errMsg.length > 0 
                                && (
                                    <>
                                        <Alert
                                            variant="danger" 
                                            dismissible >
                                            {errMsg}
                                        </Alert>
                                    </>
                                )}
                            </div>

                            <div className="form-submit">
                                <NavLink to={"/documents?refetch=true"} type="button" className="btn btn-secondary btn-cancel"  tabIndex={6} title="Annuler">
                                    <span className="icon"></span>
                                    <span className="text">Annuler</span>
                                </NavLink>
                                <Button type="button" className="btn btn-primary btn-ok" onClick={()=>{handleShow()}} tabIndex={5} title="Ajouter le nouveau média">
                                    <span className="icon"></span>
                                    <span className="text">Ajouter</span>
                                </Button>
                            </div>
                        </Form>
                    }
                </div>
            </div>
            <SubmitModal show={show} handleClose={handleClose} handleSubmit={handleSubmit} name={imageRef.current?.files?.[0]?.name || ""}/>
        </>
    );
}

function SubmitModal({show,handleClose,handleSubmit, name} : {show:boolean,handleClose:()=>void, handleSubmit:()=>void, name:string}) {
    return(
        <Modal id="mediaCreateConfirmationModal" show={show} onHide={()=>{handleClose()}}>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="mediaCreateConfirmationModalLabel">
                    <span className="icon"></span>
                    <span className="text">Création d'un média</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez créer un nouveau média&nbsp;:</p>
                <div className="object media" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                </div>
                <p>Veuillez valider la création de ce média.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    className="btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={()=>{handleClose()}}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    variant="primary"
                    className="btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={()=> {handleSubmit()}}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}